import {useState} from 'react';
import {useQuery} from '@apollo/client';
import {Spin} from 'antd';
import {toast} from 'react-toastify';
import {GET_CHART_USER_STATISTICS, GET_KYC_STATS} from '../resolvers';
import {
  Card,
  EmptyState,
  Section,
  ShareBar,
  StatCard,
  StatRow,
} from '../../shared/DashboardUI';
import DateRangeFilter, {describeRange} from '../../shared/DateRangeFilter';
import {plainNumber} from '../../shared/chartTheme';
import styles from '../../shared/dashboard.module.scss';

/**
 * Who is on the platform, and how far through verification they are.
 *
 * The KYC funnel is the part worth reading, and it used to be three columns of
 * "STATUS  count" with the status coloured and nothing else. A count on its own
 * cannot answer the only question anyone asks of a funnel — how much of the
 * base is stuck — so each status now carries its share of the total as a bar.
 */

/** Verification states, coloured by what they mean rather than by position. */
const STATUS_TONE = {
  APPROVED: '#0f8a6a',
  // Same green as APPROVED: it is an approval, and a grey bar beside the
  // approved one would read as a stalled cohort.
  CONDITIONALLY_APPROVED: '#0f8a6a',
  PENDING: '#b4530a',
  FAILED: '#c62828',
  REJECTED: '#c62828',
  NOT_INITIATED: '#8e8aa3',
  IN_PROGRESS: '#2f6bd4',
};

const statusColor = status =>
  STATUS_TONE[(status || '').toUpperCase()] ?? '#8e8aa3';

const titleCase = value =>
  (value || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

/**
 * A funnel column: every status, its count, and its share of the total.
 *
 * The endpoint appends a synthetic `TOTAL` entry to each status list. It is not
 * a state an account can be in — the old screen rendered it as one, so "Total
 * 12" sat in the column looking like a verification outcome, and summing the
 * list to get a denominator counted every account twice. It is lifted out and
 * used as the total it is.
 */
const StatusBreakdown = ({title, rows}) => {
  const all = rows ?? [];
  const declaredTotal = all.find(
    row => (row?.status || '').toUpperCase() === 'TOTAL',
  );
  const items = all.filter(
    row => (row?.status || '').toUpperCase() !== 'TOTAL',
  );
  const total =
    Number(declaredTotal?.count) ||
    items.reduce((sum, row) => sum + (Number(row?.count) || 0), 0);

  return (
    <Card>
      <div className={styles.sectionHead}>
        <div>
          <h3>{title}</h3>
        </div>
        <span className={styles.sectionMeta}>{plainNumber(total)} total</span>
      </div>
      {items.length === 0 ? (
        <EmptyState title="Nothing recorded" compact />
      ) : (
        <div className={styles.statusList}>
          {items.map((row, index) => {
            const count = Number(row?.count) || 0;
            return (
              <div key={`${row?.status}-${index}`} className={styles.statusRow}>
                <div className={styles.statusTop}>
                  <span
                    className={styles.statusLabel}
                    style={{color: statusColor(row?.status)}}>
                    {titleCase(row?.status)}
                  </span>
                  <span className={styles.statusValue}>
                    {plainNumber(count)}
                    {total > 0 && (
                      <span className={styles.statusShare}>
                        {Math.round((count * 100) / total)}%
                      </span>
                    )}
                  </span>
                </div>
                <ShareBar
                  value={count}
                  total={total}
                  color={statusColor(row?.status)}
                />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

/** A deposit cohort: how many, how much, and what it earned. */
const DepositCard = ({title, description, group}) => {
  const count = Number(group?.totalCount) || 0;
  const amount = Number(group?.statistic?.totalAmount) || 0;
  const fees = Number(group?.statistic?.totalFees) || 0;
  const symbol = group?.statistic?.symbol ?? '';

  return (
    <Card>
      <div className={styles.sectionHead}>
        <div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </div>
      <div className={styles.miniStats}>
        <div>
          <span className={styles.miniLabel}>Users</span>
          <span className={styles.miniValue}>{plainNumber(count)}</span>
        </div>
        <div>
          <span className={styles.miniLabel}>Deposited</span>
          <span className={styles.miniValue}>
            {symbol}
            {plainNumber(amount)}
          </span>
        </div>
        <div>
          <span className={styles.miniLabel}>Fees earned</span>
          <span className={styles.miniValue}>
            {symbol}
            {plainNumber(fees)}
          </span>
        </div>
      </div>
    </Card>
  );
};

export const KycStatsTab = () => {
  const [range, setRange] = useState({startDate: null, endDate: null});

  const {data, loading} = useQuery(GET_CHART_USER_STATISTICS, {
    variables: {filters: range},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load user metrics'),
  });

  const {data: kycData, loading: kycLoading} = useQuery(GET_KYC_STATS, {
    variables: {filters: range},
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    onError: () => toast.error('Could not load KYC statistics'),
  });

  const metrics = data?.getUserStatistics?.data;
  const kyc = kycData?.getKycStatistics?.data;
  const scope = describeRange(range);

  const withDeposits = metrics?.usersWithDeposits;
  const firstDeposits = metrics?.usersWhoMadeThierFirstDeposit;
  const hasDepositData =
    Number(withDeposits?.totalCount) > 0 ||
    Number(firstDeposits?.totalCount) > 0;

  const hasKyc =
    kyc?.kycStatus?.length ||
    kyc?.amlStatus?.length ||
    kyc?.documentCheckStatus?.length;

  return (
    <div className={styles.page} style={{padding: 0}}>
      <DateRangeFilter value={range} onChange={setRange} loading={loading} />

      <Section title="The base" meta={scope}>
        <StatRow columns={4}>
          <StatCard
            label="Total downloads"
            value={plainNumber(metrics?.totalUsers)}
            loading={loading}
            info="App downloads across platforms within the selected range."
          />
          <StatCard
            label="Accounts created"
            value={plainNumber(metrics?.totalUsersWithUserName)}
            hint={
              metrics?.totalUsers
                ? `${Math.round(
                    ((metrics?.totalUsersWithUserName ?? 0) * 100) /
                      metrics.totalUsers,
                  )}% of downloads`
                : undefined
            }
            loading={loading}
            info="Downloads that went on to create an account with a username."
          />
          <StatCard
            label="Active users"
            value={plainNumber(metrics?.totalActiveUsers)}
            loading={loading}
            info="Users who opened the app within the selected range."
          />
          <StatCard
            label="Active investors"
            value={plainNumber(metrics?.activeInvestors)}
            loading={loading}
            info="Users with settled orders within the selected range."
          />
        </StatRow>
      </Section>

      {hasDepositData && (
        <Section
          title="Deposits"
          description="Who is funding an account, and what that is worth."
          meta={scope}>
          <div className={styles.split}>
            <DepositCard
              title="Users with deposits"
              description="Everyone who deposited in this period"
              group={withDeposits}
            />
            <DepositCard
              title="First-time depositors"
              description="Their first deposit ever landed in this period"
              group={firstDeposits}
            />
          </div>
        </Section>
      )}

      <Section
        title="Verification"
        description="Where accounts sit in the KYC, document and AML checks."
        meta={scope}>
        {kycLoading ? (
          <div className={styles.loading}>
            <Spin />
            <span>Loading verification statistics…</span>
          </div>
        ) : hasKyc ? (
          <div className={styles.triple}>
            <StatusBreakdown title="KYC status" rows={kyc?.kycStatus} />
            <StatusBreakdown
              title="Document check"
              rows={kyc?.documentCheckStatus}
            />
            <StatusBreakdown title="AML screening" rows={kyc?.amlStatus} />
          </div>
        ) : (
          <EmptyState
            title="No verification activity in this period"
            description="Widen the date range to see accounts moving through the checks."
          />
        )}
      </Section>
    </div>
  );
};

export default KycStatsTab;
